interface User {
  id: string;
  role: string;
  subscriptionTier?: 'free' | 'pro'; // Future subscription system
}

export type ViewerType = 'basic' | 'advanced';

/**
 * Determines which image viewer to use based on user role and subscription
 * 
 * Basic Viewer (ImageViewer):
 * - Not logged in users
 * - Users with role: 'member'
 * - Users with subscription: 'free' (future)
 * 
 * Advanced Viewer (PathologyImageViewer):
 * - Users with role: 'honorary_member', 'moderator', 'admin'
 * - Users with subscription: 'pro' (future)
 */
export function getViewerType(user: User | null | undefined): ViewerType {
  // Not logged in users get basic viewer
  if (!user) {
    return 'basic';
  }

  // Future subscription-based logic (takes precedence over role)
  if (user.subscriptionTier) {
    switch (user.subscriptionTier) {
      case 'pro':
        return 'advanced';
      case 'free':
      default:
        return 'basic';
    }
  }

  // Role-based viewer selection (current implementation)
  switch (user.role) {
    // Advanced viewer for senior roles
    case 'honorary_member':
    case 'moderator':
    case 'admin':
      return 'advanced';
    
    // Basic viewer for regular members
    case 'member':
    default:
      return 'basic';
  }
}

/**
 * Check if user has access to advanced viewer features
 */
export function hasAdvancedViewerAccess(user: User | null | undefined): boolean {
  return getViewerType(user) === 'advanced';
}

/**
 * Get viewer capability description for UI display
 */
export function getViewerCapabilities(viewerType: ViewerType): {
  name: string;
  features: string[];
  greekName: string;
} {
  switch (viewerType) {
    case 'advanced':
      return {
        name: 'Advanced Slide Viewer',
        greekName: 'Προηγμένος Slide Viewer',
        features: [
          'Deep zoom up to 40x magnification',
          'Pan and navigate large images',
          'Mini-map navigator',
          'Professional zoom controls',
          'Keyboard shortcuts',
          'Touch gesture support',
          'Fullscreen mode',
          'Hardware-accelerated rendering'
        ]
      };
    
    case 'basic':
    default:
      return {
        name: 'Basic Slide Viewer',
        greekName: 'Βασικός Slide Viewer',
        features: [
          'View images in modal',
          'Navigate between images',
          'Keyboard navigation',
          'Touch-friendly controls',
          'Image metadata display'
        ]
      };
  }
}

/**
 * Get upgrade message for users with basic viewer
 */
export function getUpgradeMessage(user: User | null | undefined): string | null {
  const viewerType = getViewerType(user);
  
  if (viewerType === 'advanced') {
    return null; // No upgrade needed
  }

  if (!user) {
    return 'Συνδεθείτε για πρόσβαση σε προηγμένες δυνατότητες προβολής εικόνων';
  }

  // Future subscription upgrade message
  if (user.subscriptionTier === 'free') {
    return 'Αναβαθμίστε σε Pro συνδρομή για προηγμένες δυνατότητες παθολογικής ανάλυσης';
  }

  // Role-based message for regular members
  if (user.role === 'member') {
    return 'Προηγμένες δυνατότητες προβολής διαθέσιμες για επίτιμα μέλη και ανώτερους ρόλους';
  }

  return null;
}